import { type ReactElement, type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ConfirmDialog, Toast, type ConfirmResult, type ConfirmSpec, type ToastSpec } from './overlays';

/**
 * The confirmation + toast every console asks for (V-LIB-SHELL, D509; audit consoles F03 + F10). It is the PDV's and the
 * Merchant's byte-identical `feedback.tsx`, promoted: the Fleet console and the Agent desktop had none, so they fired
 * destructive actions (cancel a callback, close an incident, the emergency cut-off) with no confirmation at all.
 *
 *   const { confirm, toast } = useFeedback();
 *   const answer = await confirm({ title, body, confirmLabel, tone: 'danger' });   // → { confirmed, reason?, note? }
 *   if (answer.confirmed) { …; toast({ tone: 'success', message }); }
 *
 * `confirm` is the shared `ConfirmDialog` (modal: focus starts on Cancel, Tab stays inside, Escape cancels, focus returns
 * to the control that asked) behind a promise; asking again while one is open resolves the first with `confirmed: false`.
 * `toast` is the shared `Toast`: success / warning / neutral live four seconds as a `status`; a `danger` toast is an
 * `alert`, has no life bar and stays until it is dismissed or replaced by the next toast.
 * Never a native `window.confirm` / `alert` (AGENTS design rules).
 */
export interface FeedbackConfirmSpec extends Omit<ConfirmSpec, 'cancelLabel'> {
  /** Defaults to the provider's `cancelLabel`. */
  readonly cancelLabel?: string;
}

export interface FeedbackToastSpec extends Omit<ToastSpec, 'tone'> {
  readonly tone?: 'success' | 'danger' | 'neutral' | 'warning';
}

export interface FeedbackValue {
  readonly confirm: (spec: FeedbackConfirmSpec) => Promise<ConfirmResult>;
  readonly toast: (spec: FeedbackToastSpec) => void;
  /** Closes the toast on screen (a screen that moves on may want its own message gone). */
  readonly dismissToast: () => void;
}

const FeedbackContext = createContext<FeedbackValue | undefined>(undefined);

/** How long a non-`danger` toast lives (matches the life bar's 4s drain in the effect layer). */
export const FEEDBACK_TOAST_MS = 4_000;

export interface FeedbackProviderProps {
  readonly children: ReactNode;
  /** The toast's close control ("Close"). */
  readonly closeLabel: string;
  /** The default Cancel label of `confirm` ("Cancel"). */
  readonly cancelLabel: string;
  /** How long a non-`danger` toast lives (default {@link FEEDBACK_TOAST_MS}). */
  readonly toastMs?: number;
}

export function FeedbackProvider({ children, closeLabel, cancelLabel, toastMs = FEEDBACK_TOAST_MS }: FeedbackProviderProps): ReactElement {
  const [confirmSpec, setConfirmSpec] = useState<ConfirmSpec | undefined>(undefined);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const resolver = useRef<((r: ConfirmResult) => void) | undefined>(undefined);
  const [toastSpec, setToastSpec] = useState<ToastSpec | undefined>(undefined);
  const [toastOpen, setToastOpen] = useState(false);
  const defaultCancel = useRef(cancelLabel);
  defaultCancel.current = cancelLabel;

  const confirm = useCallback((spec: FeedbackConfirmSpec): Promise<ConfirmResult> => {
    resolver.current?.({ confirmed: false });
    setConfirmSpec({ ...spec, cancelLabel: spec.cancelLabel ?? defaultCancel.current });
    setConfirmOpen(true);
    return new Promise<ConfirmResult>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const onResolve = useCallback((r: ConfirmResult): void => {
    setConfirmOpen(false);
    const resolve = resolver.current;
    resolver.current = undefined;
    resolve?.(r);
  }, []);

  const toast = useCallback((spec: FeedbackToastSpec): void => {
    setToastSpec(spec);
    setToastOpen(true);
  }, []);

  const dismissToast = useCallback((): void => setToastOpen(false), []);

  // A pending confirmation never hangs a screen that unmounted under it.
  useEffect(
    () => () => {
      resolver.current?.({ confirmed: false });
      resolver.current = undefined;
    },
    [],
  );

  useEffect(() => {
    if (!toastOpen || toastSpec?.tone === 'danger') return undefined;
    const timer = window.setTimeout(() => setToastOpen(false), toastMs);
    return () => window.clearTimeout(timer);
  }, [toastOpen, toastSpec, toastMs]);

  const value = useMemo(() => ({ confirm, toast, dismissToast }), [confirm, toast, dismissToast]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <ConfirmDialog spec={confirmSpec} open={confirmOpen} onResolve={onResolve} />
      <Toast spec={toastSpec} open={toastOpen} onClose={dismissToast} closeLabel={closeLabel} />
    </FeedbackContext.Provider>
  );
}

export function useFeedback(): FeedbackValue {
  const value = useContext(FeedbackContext);
  if (value === undefined) throw new Error('useFeedback must be used within a FeedbackProvider');
  return value;
}
